import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { Http } from '../models/http.model';

@Injectable({
    providedIn: 'root',
})
export class HttpService implements Http {
    private readonly httpClient = inject(HttpClient);

    private headers!: HttpHeaders;

    public getHeaders(): HttpHeaders {
        return this.headers;
    }

    public initHeaders(locale = 'en-US'): void {
        this.headers = new HttpHeaders({
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            'Accept-Language': locale,
        });
    }

    public get$<T>(url: string): Observable<T> {
        return this.httpClient.get<T>(url, { headers: this.getHeaders() || new HttpHeaders() });
    }

    public getWithResponse$<T>(url: string): Observable<HttpResponse<T>> {
        return this.httpClient.get<T>(url, {
            headers: this.getHeaders() || new HttpHeaders(),
            observe: 'response',
        });
    }

    public getBlob$(url: string): Observable<Blob> {
        return this.httpClient.get(url, { headers: this.getHeaders() || new HttpHeaders(), responseType: 'blob' });
    }

    public getBlobWithHeaders$(url: string): Observable<HttpResponse<Blob>> {
        return this.httpClient.get(url, {
            headers: this.getHeaders() || new HttpHeaders(),
            responseType: 'blob',
            observe: 'response',
        });
    }

    public post$<T>(url: string, data: object, params: HttpParams): Observable<T> {
        return this.httpClient.post<T>(url, data, { headers: this.getHeaders() || new HttpHeaders(), params: params });
    }

    public postWithBlobResponse$(url: string, data: object, params: HttpParams): Observable<HttpResponse<Blob>> {
        return this.httpClient
            .post(url, data, { headers: this.getHeaders() || new HttpHeaders(), params, responseType: 'blob', observe: 'response' })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.error instanceof Blob) {
                        return from(error.error.text()).pipe(
                            switchMap(text => {
                                try {
                                    const json = JSON.parse(text);
                                    return throwError(
                                        () =>
                                            new HttpErrorResponse({
                                                error: json,
                                                headers: error.headers,
                                                status: error.status,
                                                statusText: error.statusText,
                                                url: error.url,
                                            })
                                    );
                                } catch {
                                    return throwError(() => error);
                                }
                            })
                        );
                    }
                    return throwError(() => error);
                })
            );
    }

    public postWithHeaders$<T>(url: string, data: object, params: HttpParams, additionalHeaders: Record<string, string>): Observable<T> {
        let headers = this.getHeaders() || new HttpHeaders();
        for (const [key, value] of Object.entries(additionalHeaders)) {
            headers = headers.set(key, value);
        }
        return this.httpClient.post<T>(url, data, { headers, params });
    }

    public put$<T>(url: string, data: object | string): Observable<T> {
        return this.httpClient.put<T>(url, data, { headers: this.getHeaders() || new HttpHeaders() });
    }

    public putWithResponse$<T = object>(url: string, data: object | string): Observable<HttpResponse<T>> {
        const headers = this.getHeaders() || new HttpHeaders();
        return this.httpClient.put<T>(url, data, { headers, observe: 'response' });
    }
    public patch$<T>(url: string, data: object): Observable<T> {
        return this.httpClient.patch<T>(url, data, { headers: this.getHeaders() || new HttpHeaders() });
    }

    public delete$<T>(url: string): Observable<T> {
        return this.httpClient.delete<T>(url, { headers: this.getHeaders() || new HttpHeaders() });
    }

    public postFile$(url: string, file: File, reportProgress?: boolean): Observable<object> {
        const formData: FormData = new FormData();
        formData.append('filesUpload', file, file.name);
        const req: HttpRequest<FormData> = new HttpRequest<FormData>('POST', url, formData, {
            reportProgress,
            headers: this.getHeaders() || new HttpHeaders(),
        });
        return this.httpClient.request<object>(req);
    }
}
